import React from 'react';
import PropTypes from 'prop-types';

import { isSvg, resolveImageUrl, fixedSources } from 'Helpers/Image/image-urls';

/**
 * FixedImg — a fixed-size <img>: logos, icons, badges.
 *
 * Which image component do I use?
 *   • FixedImg           — a fixed-size image (this one).
 *   • FluidImg           — a responsive image that fills its container.
 *   • BackgroundImg      — a CSS-style background image with content layered on top.
 *
 * Serves one source at 1x / 2x / 3x DPR variants — the browser picks purely by
 * device pixel ratio, NOT by how wide the element actually is. So "fixed" means
 * the source resolution is fixed, not that the CSS size can't vary: your CSS
 * still controls the displayed size (it may differ across breakpoints).
 *
 * Pass a relative `src` plus the CDN `folder`/`subfolder` (or an absolute URL) and
 * the URL is built for you. SVGs are served untouched.
 *
 * @param {string}  src       - Absolute URL, or file name / relative path.
 * @param {string}  width     - The LARGEST CSS px size it renders at, e.g. "140"
 *                              (so the source is never upscaled; DPR covers retina).
 * @param {string}  alt       - Alt text.
 * @param {string}  className - Extra CSS classes.
 * @param {string}  folder    - CDN folder (default 'product').
 * @param {string}  subfolder - CDN subfolder.
 * @param {boolean} lazy      - Lazy-load the image (default true).
 */
function FixedImg({ width, src, alt, className, folder, subfolder, lazy }) {
  const imgUrl = resolveImageUrl({ src, folder, subfolder });
  const loading = lazy ? 'lazy' : 'eager';

  // SVGs are vector — serve the file directly, no imgix resizing or srcSet.
  if (isSvg(src)) {
    return (
      <img
        src={imgUrl}
        alt={alt}
        className={`img-fluid img-full ${className}`}
        loading={loading}
        fetchpriority={lazy ? undefined : 'high'}
      />
    );
  }

  // One source per device-pixel-ratio so the image stays crisp on retina screens.
  const { srcSet, src: fallbackSrc } = fixedSources(imgUrl, width ? Number(width) : undefined);

  return (
    <img
      srcSet={srcSet}
      src={fallbackSrc}
      alt={alt}
      className={className}
      loading={loading}
      fetchpriority={lazy ? 'low' : 'high'}
      decoding={lazy ? 'async' : undefined}
    />
  );
}

FixedImg.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string,
  className: PropTypes.string,
  width: PropTypes.string,
  subfolder: PropTypes.string,
  folder: PropTypes.string,
  lazy: PropTypes.bool,
};

FixedImg.defaultProps = {
  alt: '',
  className: '',
  width: '',
  folder: 'product',
  subfolder: '',
  lazy: true,
};

export default FixedImg;
